/**
 * Dynamic Career Routes Component
 * Generates routes dynamically based on active job postings
 */

import { useEffect, useState } from 'react';
import { Route } from 'wouter';
import { CareerService, CareerRoute } from '@/services/careerService';
import JobApplication from '@/pages/JobApplication';
import JobPosting from '@/pages/JobPosting';

interface DynamicCareerRoutesProps {
  onRoutesLoaded?: (routes: CareerRoute[]) => void;
}

export default function DynamicCareerRoutes({ onRoutesLoaded }: DynamicCareerRoutesProps) {
  const [routes, setRoutes] = useState<CareerRoute[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRoutes = async () => {
      try {
        const careerRoutes = await CareerService.getCareerRoutes();
        setRoutes(careerRoutes);
        onRoutesLoaded?.(careerRoutes);
      } catch (error) {
        console.error('Error loading career routes:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRoutes();
  }, [onRoutesLoaded]);

  if (loading) {
    return null; // Routes will be loaded dynamically
  }

  return (
    <>
      {/* Dynamic job application routes */}
      {routes.map(route => (
        <Route 
          key={`apply-${route.id}`}
          path={route.applyRoute} 
          component={JobApplication} 
        />
      ))}
      
      {/* Dynamic job posting detail routes */}
      {routes.map(route => (
        <Route 
          key={`detail-${route.id}`}
          path={route.detailRoute} 
          component={JobPosting} 
        />
      ))}
    </>
  );
}

/**
 * Hook to get career routes
 */
export function useCareerRoutes() {
  const [routes, setRoutes] = useState<CareerRoute[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRoutes = async () => {
      try {
        const careerRoutes = await CareerService.getCareerRoutes();
        setRoutes(careerRoutes);
      } catch (error) {
        console.error('Error loading career routes:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRoutes();
  }, []);

  return { routes, loading };
}

/**
 * Hook to get job title from current URL
 */
export function useJobTitleFromUrl() {
  const [jobTitle, setJobTitle] = useState<string>('Job Application');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getJobTitle = async () => {
      try {
        const currentPath = window.location.pathname;
        const slug = currentPath.split('/').pop();
        
        if (slug && currentPath.includes('/careers/apply/')) {
          const title = await CareerService.getJobTitleFromSlug(slug);
          setJobTitle(title);
        }
      } catch (error) {
        console.error('Error getting job title from URL:', error);
      } finally {
        setLoading(false);
      }
    };

    getJobTitle();
  }, []);

  return { jobTitle, loading };
}
